/**
 * FileMakerDatasetBridge.ts - Bridge for Aggregatr dataset operations with FileMaker
 * 
 * CHANGE HISTORY:
 * - 251102_1033 [pending] danielbeer: Initial creation for dataset compilation and updates
 * 
 * Handles bidirectional dataset operations between FileMaker and OpCentral Aggregatr
 */

import { getFileMakerBridge } from './FileMakerBridge';
import type { EntityData, QueryFilters } from '@opcentral/aggregatr';

export interface DatasetCompilation {
  name: string;
  source: string;
  timestamp: number;
  records: EntityData[];
}

export interface DatasetUpdate {
  id: string;
  data: Partial<EntityData>;
}

export interface DatasetUpdateBatch {
  updates: DatasetUpdate[];
}

export class FileMakerDatasetBridge {
  private fm = getFileMakerBridge();
  private datasets: Map<string, DatasetCompilation> = new Map();

  /**
   * Register all FileMaker-callable functions for dataset operations
   */
  registerFunctions(): void {
    this.fm.registerFunctions({
      // Receive compiled dataset from FileMaker
      aggregatr_receiveDataset: this.receiveDataset.bind(this),
      
      // Request dataset compilation from FileMaker
      aggregatr_requestDataset: this.requestDataset.bind(this),
      
      // Send updates back to FileMaker
      aggregatr_sendUpdates: this.sendUpdates.bind(this),
      
      // Get dataset status
      aggregatr_getDatasetStatus: this.getDatasetStatus.bind(this),
      
      // Clear cached datasets
      aggregatr_clearCache: this.clearCache.bind(this)
    });
  }

  /**
   * Receive compiled dataset from FileMaker
   * Called by FileMaker via: aggregatr_receiveDataset(jsonString)
   */
  receiveDataset(datasetJSON: string): { success: boolean; message?: string; error?: string } {
    try {
      const dataset: DatasetCompilation = JSON.parse(datasetJSON);
      
      // Validate dataset structure
      if (!dataset.name || !dataset.source || !Array.isArray(dataset.records)) {
        return {
          success: false,
          error: 'Invalid dataset structure'
        };
      }

      // Store dataset
      this.datasets.set(dataset.name, dataset);

      // Emit event for listeners
      window.dispatchEvent(new CustomEvent('aggregatr:dataset-received', {
        detail: dataset
      }));

      return {
        success: true,
        message: `Received ${dataset.records.length} records for dataset "${dataset.name}"`
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Request dataset compilation from FileMaker
   * Triggers FileMaker script to compile and send dataset
   */
  async requestDataset(
    datasetName: string,
    options?: {
      filters?: QueryFilters;
      timeout?: number;
    }
  ): Promise<DatasetCompilation | null> {
    return new Promise((resolve) => {
      const timeout = options?.timeout || 30000;
      const timeoutId = setTimeout(() => {
        window.removeEventListener('aggregatr:dataset-received', handler);
        resolve(null);
      }, timeout);

      // Listen for dataset
      const handler = (event: Event) => {
        const customEvent = event as CustomEvent<DatasetCompilation>;
        if (customEvent.detail.name === datasetName) {
          clearTimeout(timeoutId);
          window.removeEventListener('aggregatr:dataset-received', handler);
          resolve(customEvent.detail);
        }
      };

      window.addEventListener('aggregatr:dataset-received', handler);

      // Call FileMaker to compile dataset
      this.fm.callScript('API_Handler', {
        apiMode: 'aggregatr',
        operation: 'compile',
        datasetName,
        filters: options?.filters
      });
    });
  }

  /**
   * Send updates back to FileMaker for local record updates
   */
  async sendUpdates(
    entityType: string,
    updates: DatasetUpdate[]
  ): Promise<{ success: boolean; updated?: number; error?: string }> {
    try {
      const batch: DatasetUpdateBatch = { updates };
      
      const result = await this.fm.callScriptWithResult('API_Handler', {
        apiMode: 'aggregatr',
        operation: 'update',
        entityType,
        data: batch
      });

      return result.success
        ? { success: true, updated: updates.length }
        : { success: false, error: result.error };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get cached dataset
   */
  getDataset(name: string): DatasetCompilation | null {
    return this.datasets.get(name) || null;
  }

  /**
   * Get all cached datasets
   */
  getAllDatasets(): DatasetCompilation[] {
    return Array.from(this.datasets.values());
  }

  /**
   * Get dataset status
   */
  getDatasetStatus(name: string): {
    cached: boolean;
    recordCount?: number;
    timestamp?: number;
    age?: number;
  } {
    const dataset = this.datasets.get(name);
    
    if (!dataset) {
      return { cached: false };
    }

    return {
      cached: true,
      recordCount: dataset.records.length,
      timestamp: dataset.timestamp,
      age: Date.now() - dataset.timestamp
    };
  }

  /**
   * Clear cached datasets
   */
  clearCache(datasetName?: string): { success: boolean; cleared: number } {
    if (datasetName) {
      const existed = this.datasets.has(datasetName);
      this.datasets.delete(datasetName);
      return { success: true, cleared: existed ? 1 : 0 };
    } else {
      const count = this.datasets.size;
      this.datasets.clear();
      return { success: true, cleared: count };
    }
  }

  /**
   * Compile dataset from FileMaker and process with Aggregatr
   * High-level helper that combines request + processing
   */
  async compileAndProcess(
    datasetName: string,
    processor: (dataset: DatasetCompilation) => Promise<DatasetUpdateBatch>
  ): Promise<{ success: boolean; updated?: number; error?: string }> {
    try {
      // Request dataset from FileMaker
      const dataset = await this.requestDataset(datasetName);
      
      if (!dataset) {
        return {
          success: false,
          error: 'Failed to receive dataset from FileMaker'
        };
      }

      // Process dataset
      const updates = await processor(dataset);

      // Send updates back to FileMaker
      if (updates.updates.length > 0) {
        return await this.sendUpdates(dataset.records[0]?.type || 'unknown', updates.updates);
      }

      return { success: true, updated: 0 };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Singleton instance
let datasetBridgeInstance: FileMakerDatasetBridge | null = null;

export function getFileMakerDatasetBridge(): FileMakerDatasetBridge {
  if (!datasetBridgeInstance) {
    datasetBridgeInstance = new FileMakerDatasetBridge();
    datasetBridgeInstance.registerFunctions();
  }
  return datasetBridgeInstance;
}
